import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Star, Check } from "lucide-react";
import { BUSINESS, IMAGES, LOGO_TRANSPARENT } from "@/lib/site";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const heroImageAnim = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const heroBadgeAnim = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.5, duration: 0.6 },
};

export const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
        <div>
          <motion.img
            {...fade(0)}
            src={LOGO_TRANSPARENT}
            alt={BUSINESS.fullName}
            data-testid="hero-logo"
            className="h-32 sm:h-44 w-auto -ml-2 mb-4"
          />
          <motion.div {...fade(0.04)} className="inline-flex items-center gap-2 rounded-full bg-sand border border-line px-4 py-2 mb-6">
            <span className="flex">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={`hero-star-${n}`} className="w-3.5 h-3.5 fill-lawn text-lawn" />
              ))}
            </span>
            <span className="text-xs font-bold text-ink-soft tracking-wide">Trusted by Bendigo homeowners</span>
          </motion.div>

          <motion.h1 {...fade(0.08)} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tight text-ink">
            We keep your property <span className="text-forest">looking its best</span>
          </motion.h1>

          <motion.p {...fade(0.16)} className="mt-6 text-base sm:text-lg text-ink-soft max-w-lg leading-relaxed">
            {BUSINESS.blurb} Get an instant quote estimate in under a minute — no waiting around.
          </motion.p>

          <motion.div {...fade(0.24)} className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-get-quote"
              onClick={() => navigate("/quote")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-8 py-4 text-base font-bold hover:bg-forest-dark transition-colors"
            >
              Get My Instant Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="hero-call"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-forest/20 text-forest px-8 py-4 text-base font-bold hover:border-forest transition-colors"
            >
              Call {BUSINESS.phone}
            </a>
          </motion.div>

          <motion.div {...fade(0.32)} className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {["Free instant quotes", "Honest, upfront pricing", "Fully insured", "Local to Bendigo"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm font-semibold text-ink-soft">
                <Check className="w-4 h-4 text-lawn" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          {...heroImageAnim}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-forest/20 aspect-[4/5] lg:aspect-[4/4.4]">
            <img src={IMAGES.hero} alt="Freshly mowed lawn in front of a classic weatherboard home" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/40 to-transparent" />
          </div>
          <motion.div
            {...heroBadgeAnim}
            className="absolute -bottom-5 -left-3 sm:left-6 bg-cream rounded-2xl border border-line shadow-xl p-4 pr-6"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-ink-soft">Trusted local team</p>
            <p className="font-heading text-2xl font-extrabold text-forest">Quality, guaranteed</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
